<script setup lang="ts">
import { useBillingRequests } from '@/composables/billing/useBillingRequests';
import { useBillingMutations } from '@/composables/billing/useBillingMutations';
import { Components } from '@/api/generated/api';
import { useBillingState } from '@/composables/billing/useBillingState';
import CustomButton from '@/core/elements/CustomButton.vue';
import IconButton from '@/core/elements/IconButton.vue';
import LoadingSpinner from '@/core/elements/LoadingSpinner.vue';
import DialogBase from '@/core/dialogs/base-dialog/DialogBase.vue';
import BillingDialogHeader from '@/core/dialogs/billing-checkout/BillingDialogHeader.vue';
import BillingCheckoutInvoiceAddressStep from '@/core/dialogs/billing-checkout/BillingCheckoutInvoiceAddressStep.vue';
import BillingCheckoutPlanSelectionStep from '@/core/dialogs/billing-checkout/BillingCheckoutPlanSelectionStep.vue';
import BillingSuccessPanel from '@/core/dialogs/billing-checkout/BillingSuccessPanel.vue';
import { getBillingInvoiceAddressDraft, type BillingInterval } from '@/utils/billing';
import { EIcon } from 'common/enum/misc.enum';
import { nextTick, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

enum BillingOfferStep {
  Plan = 'plan',
  Address = 'address',
  Success = 'success',
}

const { t } = useI18n();
const props = defineProps<{
  viewBillingDocuments?: () => void;
}>();
const { canManageBilling, billingStatus } = useBillingState();
const { getTenantInvoiceAddress } = useBillingRequests(canManageBilling);
const { createTenantOffer } = useBillingMutations(billingStatus);

const isOpen = ref(false);
const dialogRef = useTemplateRef('dialogRef');
const currentStep = ref<BillingOfferStep>(BillingOfferStep.Plan);
const isSubmitting = ref(false);
const selectedPlan = ref<Components.Schemas.ProductTypeDto | undefined>();
const selectedQuantity = ref<number | undefined>();
const selectedTeamQuantity = ref<number | undefined>();
const selectedStudentQuantity = ref<number | undefined>();
const selectedBillingInterval = ref<BillingInterval>('year');
const checkoutSource = ref<Components.Schemas.CheckoutSource>('default');
const invoiceAddress = ref<Components.Schemas.BillingInvoiceAddressDto | null>(null);
const invoiceAddressDraft = ref<Components.Schemas.UpsertTenantInvoiceAddressDto | null>(null);
const createdOffer = ref<Components.Schemas.BillingOfferDto | null>(null);

const show = (
  params: {
    initialPlan?: Components.Schemas.ProductTypeDto;
    initialInterval?: BillingInterval;
    initialTeamQuantity?: number;
    initialStudentQuantity?: number;
    checkoutSource?: Components.Schemas.CheckoutSource;
  } = {},
): void => {
  isOpen.value = true;
  currentStep.value = BillingOfferStep.Plan;
  selectedPlan.value = params.initialPlan;
  selectedQuantity.value = undefined;
  selectedTeamQuantity.value = params.initialTeamQuantity;
  selectedStudentQuantity.value = params.initialStudentQuantity;
  selectedBillingInterval.value = params.initialInterval ?? 'year';
  checkoutSource.value = params.checkoutSource ?? (billingStatus.value?.isTrialActive ? 'trialUpsell' : 'default');
  invoiceAddress.value = null;
  invoiceAddressDraft.value = null;
  createdOffer.value = null;
  dialogRef.value?.show();
};

const hide = (): void => {
  if (isSubmitting.value) return;
  dialogRef.value?.hide();
};

const handlePlanSelectionQuantityDraftChange = (params: { teamQuantity: number; studentQuantity: number }): void => {
  selectedTeamQuantity.value = params.teamQuantity;
  selectedStudentQuantity.value = params.studentQuantity;
};

const handlePlanSelectionPlanDraftChange = (plan: Components.Schemas.ProductTypeDto): void => {
  selectedPlan.value = plan;
};

const handlePlanSelectionIntervalDraftChange = (billingInterval: BillingInterval): void => {
  selectedBillingInterval.value = billingInterval;
};

const handlePlanSelectionStepDone = async (params: {
  plan: Components.Schemas.ProductTypeDto;
  quantity?: number;
  billingInterval: BillingInterval;
}): Promise<void> => {
  if (!canManageBilling.value || isSubmitting.value) return;
  if (params.billingInterval !== 'year') return;
  selectedPlan.value = params.plan;
  selectedQuantity.value = params.quantity;
  selectedBillingInterval.value = params.billingInterval;

  try {
    isSubmitting.value = true;
    invoiceAddress.value = await getTenantInvoiceAddress();
    invoiceAddressDraft.value ??= getBillingInvoiceAddressDraft(invoiceAddress.value);
    currentStep.value = BillingOfferStep.Address;
  } finally {
    isSubmitting.value = false;
  }
};

const handleAddressStepDone = async (address: Components.Schemas.UpsertTenantInvoiceAddressDto): Promise<void> => {
  if (!canManageBilling.value || isSubmitting.value || !selectedPlan.value) return;

  try {
    isSubmitting.value = true;
    invoiceAddressDraft.value = address;
    createdOffer.value = await createTenantOffer(selectedPlan.value.id, address, {
      quantity: selectedQuantity.value,
    });
    if (createdOffer.value) currentStep.value = BillingOfferStep.Success;
  } finally {
    isSubmitting.value = false;
  }
};

const viewBillingDocuments = (): void => {
  hide();
  props.viewBillingDocuments?.();
};

watch(currentStep, async () => {
  await nextTick();
  dialogRef.value?.scrollToTop();
});

defineExpose({
  show,
});
</script>

<template>
  <DialogBase
    ref="dialogRef"
    :show-header="false"
    enter-animation="slide-up"
    :content-width-style="
      currentStep === BillingOfferStep.Plan
        ? 'w-[916px] max-w-[calc(100vw-32px)] max-md:w-screen max-md:max-w-full'
        : 'w-[960px] max-w-[calc(100vw-32px)] max-md:w-screen max-md:max-w-full'
    "
    no-padding
    :dialog-height-style="
      currentStep === BillingOfferStep.Plan
        ? 'h-[638px] !max-h-[92vh] max-md:h-dvh max-md:!max-h-dvh'
        : 'h-[640px] !max-h-[92vh] max-md:h-dvh max-md:!max-h-dvh'
    "
    :disable-internal-hide-logic="isSubmitting"
    @hide="isOpen = false"
  >
    <template #above-header>
      <BillingDialogHeader
        v-if="currentStep === BillingOfferStep.Plan"
        :title="t('billing.offers.createTitle')"
        @close="hide"
      />
    </template>

    <div class="relative h-full">
      <BillingCheckoutPlanSelectionStep
        v-if="currentStep === BillingOfferStep.Plan"
        :active="isOpen"
        :initial-plan="selectedPlan"
        :initial-interval="selectedBillingInterval"
        :initial-team-quantity="selectedTeamQuantity"
        :initial-student-quantity="selectedStudentQuantity"
        :checkout-source="checkoutSource"
        :disabled="isSubmitting"
        block-monthly-continue
        :monthly-continue-blocked-hint="t('billing.offers.monthlyUnavailableHint')"
        @continue="handlePlanSelectionStepDone"
        @plan-draft-change="handlePlanSelectionPlanDraftChange"
        @interval-draft-change="handlePlanSelectionIntervalDraftChange"
        @quantity-draft-change="handlePlanSelectionQuantityDraftChange"
      />

      <BillingCheckoutInvoiceAddressStep
        v-if="currentStep === BillingOfferStep.Address"
        :address="invoiceAddress"
        :draft-address="invoiceAddressDraft"
        :disabled="isSubmitting"
        :title="t('billing.offers.addressTitle')"
        :submit-label="t('billing.offers.createOffer')"
        @back="currentStep = BillingOfferStep.Plan"
        @continue="handleAddressStepDone"
        @draft-change="invoiceAddressDraft = $event"
      />

      <BillingSuccessPanel
        v-if="currentStep === BillingOfferStep.Success"
        :title="t('billing.offers.successTitle')"
        :description="t('billing.offers.successDescription')"
        :body="t('billing.offers.successDetails')"
      >
        <template #footer>
          <CustomButton type="button" theme="secondary" :disabled="isSubmitting" @click="viewBillingDocuments">
            {{ t('billing.offers.viewOffer') }}
          </CustomButton>
          <CustomButton class="w-min justify-center whitespace-nowrap" :disabled="isSubmitting" @click="hide">
            {{ t('general.done') }}
          </CustomButton>
        </template>
      </BillingSuccessPanel>

      <IconButton
        v-if="currentStep !== BillingOfferStep.Plan && currentStep !== BillingOfferStep.Success"
        class="absolute right-lg top-lg z-10 max-md:right-md max-md:top-md"
        type="button"
        size="button"
        :icon-name="EIcon.X"
        theme="text"
        @click="hide"
      />
    </div>
    <LoadingSpinner v-if="isSubmitting" />
  </DialogBase>
</template>
